import { Routes } from '@angular/router';
import { MainLayout } from './layout/main-layout/main-layout';
import { AuthLayout } from './layout/auth-layout/auth-layout';
import { authGuard } from './core/guards/auth-guard';
import { permissionGuard } from './core/guards/permission-guard';
import { policyAcknowledgementGuard } from './core/guards/policy-acknowledgement-guard';
import { noAuthGuard } from './core/guards/no-auth.guard';
import { nonProdGuard } from './core/guards/non-prod-guard';
import { superAdminGuard } from './core/guards/super-admin.guard';
import { RULE_SET_SERVICE } from './services/rule-set-service.token';
import { RuleSetService } from './services/rule-set.service';

export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  // Deploy-visibility route, outside the auth/main layouts (no login needed) -
  // dev/QA/UAT only, see nonProdGuard.
  {
    path: 'server-info',
    canActivate: [nonProdGuard],
    loadComponent: () => import('./features/server-info/server-info').then((m) => m.ServerInfo),
  },
  {
    path: '',
    component: AuthLayout,
    canActivate: [noAuthGuard],
    loadChildren: () => import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  // Full-screen takeover, deliberately outside MainLayout (no sidebar/header -
  // MainLayout always renders both, with no per-route way to suppress them).
  // Only authGuard applies here - policyAcknowledgementGuard would create a
  // redirect loop against its own destination.
  {
    path: 'policy-acknowledgement',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/policy-acknowledgement/policy-acknowledgement').then(
        (m) => m.PolicyAcknowledgement,
      ),
  },
  {
    path: '',
    component: MainLayout,
    canActivate: [authGuard],
    canActivateChild: [policyAcknowledgementGuard, permissionGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES),
      },
      {
        // Super-Admin-only screen (Menu Configuration) - see super-admin.guard.ts.
        path: 'menus',
        canActivate: [superAdminGuard],
        canActivateChild: [superAdminGuard],
        loadChildren: () => import('./features/menus/menus.routes').then((m) => m.MENUS_ROUTES),
      },
      {
        // Super-Admin-only screen (Client Configuration) - see super-admin.guard.ts.
        path: 'client',
        canActivate: [superAdminGuard],
        canActivateChild: [superAdminGuard],
        loadChildren: () =>
          import('./features/components/client/client.routes').then((m) => m.CLIENT_ROUTES),
      },
      {
        path: 'email-log',
        loadChildren: () =>
          import('./features/components/email-log/email-log.routes').then(
            (m) => m.EMAIL_LOG_ROUTES,
          ),
      },
      {
        path: 'admin-master',
        loadChildren: () =>
          import('./features/components/admin-master/admin-master.routes').then(
            (m) => m.ADMIN_MASTER_ROUTES,
          ),
      },




      {
        path: 'master/code-group',
        loadComponent: () =>
          import('./features/components/admin-master/code-group-list/code-group-list.component').then(
            (m) => m.CodeGroupListComponent
          ),
        data: { formId: 'code-group', modelName: 'CodeGroupModel', title: 'Code Group' },
        providers: [
          { provide: RULE_SET_SERVICE, useClass: RuleSetService }
        ],
        children: [
          {
            path: 'new',
            loadComponent: () =>
              import('./features/components/admin-master/code-group-form/code-group-form').then(
                (m) => m.CodeGroupForm
              ),
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('./features/components/admin-master/code-group-form/code-group-form').then(
                (m) => m.CodeGroupForm
              ),
          },
        ],
      },
      {
        path: 'personal-master',
        loadChildren: () =>
          import('./features/components/personal-master/personal-master.routes').then(
            (m) => m.PERSONAL_MASTER_ROUTES,
          ),
      },
      {
        path: 'benefits',
        loadChildren: () =>
          import('./features/components/benefits/benefits.routes').then(
            (m) => m.BENEFITS_ROUTES,
          ),
      },
      {
        path: 'policy-group',
        loadChildren: () =>
          import('./features/components/policy-group/policy-group.routes').then(
            (m) => m.POLICY_GROUP_ROUTES,
          ),
      },
      {
        path: 'ess',
        loadChildren: () =>
          import('./features/components/ess/ess.routes').then((m) => m.ESS_ROUTES),
      },
      {
        path: 'reports',
        loadChildren: () =>
          import('./features/components/reports/reports.routes').then((m) => m.REPORTS_ROUTES),
      },
      {
        path: 'payroll',
        loadChildren: () =>
          import('./features/components/payroll/payroll.routes').then((m) => m.PAYROLL_ROUTES),
      },
      {
        path: 'policy-group',
        loadChildren: () =>
          import('./features/components/policy-group/policy-group.routes').then(
            (m) => m.POLICY_GROUP_ROUTES,
          ),
      },
      {
        path: 'requests',
        loadChildren: () =>
          import('./features/components/requests/requests.routes').then((m) => m.REQUESTS_ROUTES),
      },
      // Legacy menu link for Letter Request (menus.navigation_url, id 121) —
      // screen now lives under /requests/letter.
      {
        path: 'app/employee/request-letter',
        redirectTo: 'requests/letter',
        pathMatch: 'full',
      },
      {
        path: 'app/leave/leave-encashment/list',
        redirectTo: 'requests/encashment',
        pathMatch: 'full',
      },
      {
        path: 'app/leave/leave-encashment/list',
        redirectTo: 'requests/encashment',
        pathMatch: 'full',
      },
      {
        path: 'approve',
        loadChildren: () =>
          import('./features/components/approve/approve.routes').then((m) => m.APPROVE_ROUTES),
      },
      {
        path: 'tna',
        loadChildren: () =>
          import('./features/components/tna/tna.routes').then((m) => m.TNA_ROUTES),
      },
      {
        path: 'my-team',
        loadChildren: () =>
          import('./features/components/my-team/my-team.routes').then((m) => m.MY_TEAM_ROUTES),
      },
      {
        path: 'myteam',
        loadChildren: () =>
          import('./features/components/my-team/my-team.routes').then((m) => m.MY_TEAM_ROUTES),
      },
      {
        path: 'communication',
        loadChildren: () =>
          import('./features/components/communication/communication.routes').then(
            (m) => m.COMMUNICATION_ROUTES,
          ),
      },

      {
        path: 'access-denied',
        loadComponent: () =>
          import('./features/access-denied/pages/access-denied/access-denied').then(
            (m) => m.AccessDenied,
          ),
      },
    ],
  },
  { path: '**', redirectTo: 'login' },
];
